/**
 * Home (M1): the guest hero from designs/Caged Home ("Play caged." + Quick fight) with the level
 * widgets from Caged Fighter Level / Home Signed In, links to Training and the league (and the
 * Weekly Tourney, coming soon), and the bot difficulty. Desktop per Caged Home Desktop / Home
 * Signed In Desktop.
 */
import { availablePoints, FIGHT, type BotDifficulty } from '@caged/shared';
import { useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { onlineConfigured } from '../../net/config';
import { useLeague } from '../../state/league';
import { useApp } from '../../state/store';
import { AccountButtons, useAccountButtonsShown } from '../components/AccountButtons';
import { AvatarButton, DesktopHeader, MenuButton, PageFooter, WalletChip } from '../components/Chrome';
import { Segmented } from '../components/Controls';
import { HeroVideo } from '../components/HeroVideo';
import { LeagueFightCard, LeagueFightCardPlaceholder, useLeagueFightShown } from '../components/LeagueFightCard';
import { LeagueHeroChip } from '../components/LeagueNavBadge';
import { LevelCard, useDuration, WeeklyCard } from '../components/ProgressWidgets';
import ui from '../components/ui.module.css';
import { Logo } from '../components/Logo';
import { readBootHint, writeBootHint } from '../bootHint';
import { useLayoutMode, useNow, useRegistered, useScrollTop } from '../hooks';
import { fightPhase, Flag, useSeriesTitle } from '../league';
import { currentProgress, randomFighterName } from '../progression';
import '../ui.css';
import s from './HomeScreen.module.css';

function useHomeData() {
  const progress = useApp((st) => st.progress);
  const now = useNow(30_000);
  const p = useMemo(() => currentProgress(progress, now), [progress, now]);
  return { p, now };
}

/**
 * The league fight card's spot (D50): while a registered player's league loads, a placeholder
 * where the card showed last time, so the card doesn't push Home around when it comes. `rise`:
 * whether the card comes in with its entrance (not when it takes the placeholder's place).
 */
function useLeagueCardSpot(): { card: boolean; placeholder: boolean; rise: boolean } {
  const registered = useRegistered();
  const card = useLeagueFightShown();
  const settled = useLeague((st) => st.mine !== null || st.failed);
  const [hinted] = useState(() => readBootHint().league);
  const placeholder = registered && hinted && !settled;
  const [rise] = useState(!placeholder);
  return { card, placeholder, rise };
}

/** Home's shape for the next visit's loading skeleton (ui/bootHint.ts). */
function useBootHint() {
  const buttons = useAccountButtonsShown();
  const registered = useRegistered();
  const mine = useLeague((st) => st.mine);
  const league = useLeagueFightShown();
  useEffect(() => {
    // A registered player's league card is known once their league has loaded.
    if (registered && !mine) return;
    writeBootHint({ buttons, league });
  }, [buttons, registered, mine, league]);
}

function DifficultyToggle({ size = 'sm' }: { size?: 'sm' | 'md' }) {
  const { t } = useTranslation();
  const difficulty = useApp((st) => st.settings.difficulty);
  const updateSettings = useApp((st) => st.updateSettings);
  return (
    <Segmented<BotDifficulty>
      size={size}
      label={t('screens.home.difficulty')}
      value={difficulty === 'easy' ? 'easy' : 'normal'}
      options={[
        { value: 'easy', label: t('screens.difficulty.easy') },
        { value: 'normal', label: t('screens.difficulty.normal') },
      ]}
      onChange={(v) => updateSettings({ difficulty: v })}
    />
  );
}

function QuickFightButton({ big = false }: { big?: boolean }) {
  const { t } = useTranslation();
  const startQuickFight = useApp((st) => st.startQuickFight);
  return (
    <button type="button" className={[ui.primary, big ? s.ctaBig : ''].join(' ')} onClick={() => startQuickFight()}>
      <span className={s.ctaText}>
        {t('screens.home.quickFight')}
        {big && (
          <span className={s.ctaSub}>
            {/* Online: a human first, a bot fills in (D17); offline: always the local bot. */}
            {t(onlineConfigured() ? 'screens.home.online' : 'screens.home.vsBot', { rounds: FIGHT.rounds, seconds: FIGHT.roundSeconds })}
          </span>
        )}
      </span>
      <span className={ui.arrow} aria-hidden="true">
        ▷
      </span>
    </button>
  );
}

/** Whether this registered player ticked "I am 18 or older" at signup (O7); asked once per visit. */
function useAdult(registered: boolean): boolean {
  const [adult, setAdult] = useState(false);
  useEffect(() => {
    if (!registered) return;
    let live = true;
    void import('../../net/progressApi')
      .then((api) => api.fetchProfileRemote())
      .then((p) => live && setAdult(p.adult))
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [registered]);
  return registered && adult;
}

/**
 * The links with news on them: points to spend and the league, plus the Weekly Tourney (M6) to
 * come. Profile and Settings are a tap away in the top bar (the avatar and the menu on phones, the
 * navigation on desktop).
 */
function Links({ wide = false }: { wide?: boolean }) {
  const { t } = useTranslation();
  const go = useApp((st) => st.go);
  const points = useApp((st) => availablePoints(st.progress));
  const registered = useRegistered();
  // The tourney is sponsor and prize content: registered 18+ players only, never a guest (A2).
  const adult = useAdult(registered);
  return (
    <nav className={[s.links, wide ? s.linksWide : ''].join(' ')} aria-label={t('screens.nav.label')}>
      <button type="button" className={[s.link, points > 0 ? s.linkHot : ''].join(' ')} onClick={() => go('training')}>
        <span className={ui.label}>{t('screens.nav.training')}</span>
        <span className={s.linkRow}>
          <span className={s.linkTitle}>
            {points > 0 ? (
              <Trans i18nKey="screens.home.trainPoints" count={points} components={{ g: <span className={s.green} /> }} />
            ) : (
              t('screens.home.trainSkills')
            )}
          </span>
          <span className={s.linkArrow} aria-hidden="true">
            →
          </span>
        </span>
      </button>
      <LeagueLink registered={registered} />
      {adult && (
        <div className={[s.link, s.linkSoon].join(' ')}>
          <span className={ui.label}>{t('screens.home.tourneyLabel')}</span>
          <span className={s.linkTitle}>{t('screens.home.tourneySoon')}</span>
        </div>
      )}
    </nav>
  );
}

/** The country league (M5): today's fight for a registered player; an invitation for a guest. */
function LeagueLink({ registered }: { registered: boolean }) {
  const { t } = useTranslation();
  const openLeague = useApp((st) => st.openLeague);
  const mine = useLeague((st) => st.mine);
  const load = useLeague((st) => st.load);
  const title = useSeriesTitle();
  const duration = useDuration();
  const now = useNow(30_000);
  useEffect(() => {
    if (registered) void load();
  }, [registered, load]);
  const own = registered && mine?.status === 'ok' ? mine : null;
  const me = own?.table.find((r) => r.me);
  // The league card above shows the fight and its countdown (D50): this link is the standings.
  const card = useLeagueFightShown();
  const phase = own && !card ? fightPhase(own.next, now) : 'none';
  return (
    <button type="button" className={[s.link, phase === 'live' ? s.linkHot : ''].join(' ')} onClick={() => openLeague('league')}>
      <span className={ui.label}>
        {own ? (
          <>
            <Flag country={own.country} /> {title(own.series)}
          </>
        ) : (
          t('screens.home.leaguesLabel')
        )}
      </span>
      <span className={s.linkRow}>
        <span className={s.linkTitle}>{own && me ? t('screens.league.posPts', { pos: me.position, pts: me.points }) : t('screens.home.leaguesTitle')}</span>
        <span className={s.linkEnd}>
          {own && phase !== 'none' && (
            <span className={[s.soonChip, phase === 'live' ? s.liveChip : ''].join(' ')}>
              {phase === 'live'
                ? t('screens.home.leagueLive')
                : phase === 'upcoming'
                  ? t('screens.home.leagueFightIn', { time: duration(own.next.fightAt - now) })
                  : t('screens.home.leagueResults')}
            </span>
          )}
          <span className={s.linkArrow} aria-hidden="true">
            →
          </span>
        </span>
      </span>
    </button>
  );
}

function HomePhone() {
  const { t } = useTranslation();
  const upright = useLayoutMode() === 'portrait';
  const { p, now } = useHomeData();
  const league = useLeagueCardSpot();
  return (
    <div className={[ui.column, s.phone].join(' ')}>
      <header className={s.topBar}>
        <Logo size={30} />
        <div className={s.topRight}>
          <WalletChip />
          <AvatarButton />
          <MenuButton />
        </div>
      </header>

      <section className={s.hero} aria-labelledby="home-title">
        <div className={s.heroStage}>
          {/* Sideways the buttons sit on the right, so the wide video is mirrored: fighters left. */}
          <HeroVideo
            shape={upright ? 'tall' : 'wide'}
            mirror={!upright}
            shade="linear-gradient(180deg,rgba(20,19,18,.85),rgba(20,19,18,.2) 40%,rgba(20,19,18,.35) 70%,rgba(20,19,18,.95)),linear-gradient(rgba(20,19,18,.2),rgba(20,19,18,.2))"
          />
          <h1 id="home-title" className={s.heroTitle}>
            <span>{t('screens.home.play')}</span>
            <span className={s.amber}>{t('screens.home.caged')}</span>
          </h1>
          {/* The next league fight's countdown, above the fold (D50); the card below has the rest. */}
          <LeagueHeroChip className={s.heroChip} />
          <div className={s.heroActions}>
            <QuickFightButton />
            <AccountButtons />
          </div>
        </div>
        <div className={s.heroStrip}>
          <span className={ui.label}>{t('screens.home.difficulty')}</span>
          <DifficultyToggle />
        </div>
      </section>

      <div className={s.stack}>
        {league.placeholder ? <LeagueFightCardPlaceholder /> : <LeagueFightCard rise={league.rise} />}
        <LevelCard progress={p} now={now} />
        <WeeklyCard progress={p} now={now} />
        <Links />
      </div>
      <PageFooter />
    </div>
  );
}

function HomeDesktop() {
  const { t } = useTranslation();
  const { p, now } = useHomeData();
  // With a league fight ahead, it takes the side column (designs/Caged Home Signed In Desktop, D50).
  const league = useLeagueCardSpot();
  const fight = league.card || league.placeholder;
  return (
    <>
      <DesktopHeader />
      <main className={s.deskMain}>
        <section className={s.deskHero} aria-labelledby="home-title">
          <HeroVideo
            shape="wide"
            fitWidth
            shade="linear-gradient(90deg,rgba(20,19,18,.94),rgba(20,19,18,.75) 35%,rgba(20,19,18,.2) 60%,rgba(20,19,18,.05) 80%),linear-gradient(180deg,rgba(20,19,18,.45),transparent 30%,transparent 70%,rgba(20,19,18,.85)),linear-gradient(rgba(20,19,18,.12),rgba(20,19,18,.12))"
          />
          <div className={s.deskHeroContent}>
            <div className={s.deskHeroTop}>
              <span className={ui.label}>{t('screens.home.tagline')}</span>
              <h1 id="home-title" className={s.deskTitle}>
                <span>{t('screens.home.play')}</span>
                <span className={s.amber}>{t('screens.home.caged')}</span>
              </h1>
            </div>
            <div className={s.deskActions}>
              <QuickFightButton big />
              <AccountButtons />
              <div className={s.deskDifficulty}>
                <span className={ui.label}>{t('screens.home.difficulty')}</span>
                <DifficultyToggle />
              </div>
            </div>
          </div>
        </section>
        <div className={s.deskSide}>
          {league.placeholder ? (
            <LeagueFightCardPlaceholder size="lg" />
          ) : fight ? (
            <LeagueFightCard size="lg" rise={league.rise} />
          ) : (
            <>
              <LevelCard progress={p} now={now} size="lg" />
              <WeeklyCard progress={p} now={now} size="lg" />
            </>
          )}
        </div>
      </main>
      {fight && (
        <div className={s.deskCards}>
          <LevelCard progress={p} now={now} size="lg" />
          <WeeklyCard progress={p} now={now} size="lg" />
        </div>
      )}
      <div className={s.deskLinks}>
        <Links wide />
      </div>
      <PageFooter wide />
    </>
  );
}

export function HomeScreen() {
  const mode = useLayoutMode();
  const homeOpened = useApp((st) => st.homeOpened);
  const ensurePlayerName = useApp((st) => st.ensurePlayerName);
  useScrollTop();
  useBootHint();
  useEffect(() => {
    ensurePlayerName(randomFighterName());
    homeOpened();
  }, [homeOpened, ensurePlayerName]);
  return <div className={['cg-ui', ui.screen, s.root].join(' ')}>{mode === 'desktop' ? <HomeDesktop /> : <HomePhone />}</div>;
}
